import { GetServerSideProps } from "next";

import { Ogp } from "shared/features/utils";
import { getParam } from "shared/lib/next";
import { MagazineArticleType } from "shared/models";
import { NextPageWithLayout } from "shared/types";

import { StandardLayout } from "~/components/layouts";
import {
  MagazineArticleShowPage,
  StrapiArticle,
  StrapiArticleBody,
  StrapiResponse,
  strapiFetch,
  pickBodyText,
  toStrapiImageURL,
} from "~/features/magazines";

type Props = {
  slug: string;
  article: Partial<MagazineArticleType>;
};

const Page: NextPageWithLayout<Props> = ({
  slug,
  article,
}) => {
  const { blocks } = JSON.parse(article.body || "{ blocks: [] }") as StrapiArticleBody;

  return (
    <>
      <Ogp
        title={`${article.title} | SocTocマガジン`}
        description={pickBodyText(blocks)}
        image={article.cover?.url}
      />
      <MagazineArticleShowPage slug={slug} article={article} />
    </>
  );
};

Page.getLayout = (page) => (
  <StandardLayout>{page}</StandardLayout>
);

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
  const { params = {} } = context;
  const slug = getParam(params, "slug");

  const { data } = await strapiFetch(
    "/api/articles",
    {
      query: {
        populate: "*",
        filters: {
          slug: {
            $eq: slug,
          },
        },
        publicationState: "preview",
      },
    },
  ).then((res) => res.json() as Promise<StrapiResponse<StrapiArticle[]>>);

  const article = data.shift();
  if (!article) {
    return {
      notFound: true,
    };
  }

  const { id, attributes } = article;

  return {
    props: {
      slug,
      article: {
        id: id,
        title: attributes.title,
        body: attributes.body,
        cover: attributes.cover ? {
          url: toStrapiImageURL(attributes.cover.data.attributes.url),
          width: attributes.cover.data.attributes.width,
          height: attributes.cover.data.attributes.height,
        } : undefined,
        publishAt: new Date(attributes.publishAt || attributes.createdAt),
      },
    },
  };
};

export default Page;
